/**
 * Pricing — FAMILY_AUTH D8, revised 2026-08-12 (jp).
 *
 * **There is no free tier.** monkeydoc and monkeytab are what you buy; they
 * were briefly specced as free and auto-granted at signup, and that is gone.
 * An account on its own grants nothing — it exists so we can sell to you and so
 * the family CLIs can identify you.
 *
 * The consequence worth noting: with nothing free, we never read anyone's
 * content, so the "free tier trades your data" language the page used to carry
 * describes a tier that no longer exists. It has been removed rather than
 * softened.
 *
 * Shared by the server view and the client poll widget so the two cannot drift.
 */

export interface PriceOption {
  id: 'shared' | 'once' | 'year'
  name: string
  price: string
  period?: string
  pitch: string
  points: string[]
  caveat?: string
  highlight?: boolean
}

export const PRICE_OPTIONS: PriceOption[] = [
  {
    // ⚠️ The one plan where we receive customer content. Owner ruling, jp,
    // 2026-08-12. Every word of this card is load-bearing: it is the only place
    // a buyer learns the trade before paying, and "cheap tier quietly takes your
    // files" is the single worst thing that could be said about this product.
    // Do not soften it to improve conversion.
    id: 'shared',
    name: 'Contributor',
    price: '$1',
    pitch: 'A tenth of the price, because you share your work with our research.',
    points: [
      'monkeydoc + monkeytab, this version, yours permanently',
      '⚠️ Your documents and tables are sent to us and studied',
      'Everything else on this page stays true: we never sell them or train ads on them',
      'Change your mind at any time — sharing stops, and the $1 licence ends with it',
    ],
    caveat:
      'This is the only plan where your work leaves your machine — on every other plan we never receive it at all. The $1 price exists because of the data: stop sharing and the licence ends, though you can upgrade to $10 and carry on. Your files are always yours either way.',
  },
  {
    id: 'once',
    name: 'This version',
    price: '$10',
    pitch: 'Buy it once. It is yours, at this version, forever.',
    points: [
      'monkeydoc + monkeytab, desktop and web',
      'Yours permanently — it keeps working if we vanish',
      'No upgrades included',
      'We never read your content',
    ],
  },
  {
    id: 'year',
    name: 'A year of upgrades',
    price: '$100',
    period: '/year',
    pitch: 'Everything we ship for twelve months, yours to keep after.',
    points: [
      'monkeydoc + monkeytab, desktop and web',
      'Every upgrade for 12 months',
      'What you have keeps working when the year ends',
      'We never read your content',
    ],
    highlight: true,
  },
]

/** Ballot options. Deliberately includes choices the cards don't sell. */
export interface PollOption {
  id: string
  label: string
}

export const POLL_OPTIONS: PollOption[] = [
  { id: 'shared', label: '$1 with my work shared for research — I’d take that trade' },
  { id: 'once', label: '$10 once — this version, no upgrades' },
  { id: 'year', label: '$100 a year — everything we ship' },
  { id: 'cheaper-year', label: 'The yearly idea, but cheaper' },
  { id: 'monthly', label: 'I’d rather pay monthly' },
  { id: 'none', label: 'None of these (I’ll say why)' },
]

export const POLL_ID = 'pricing-2026-08'
